import { useEffect, useRef } from 'react';

/**
 * Sends the window back to the top when the route changes, so following a
 * link from halfway down a long list (the cEDH meta page, a deck list) opens
 * the next page at its start instead of at the old page's scroll offset.
 *
 * Deliberately narrow about when it fires:
 *   - Only on a pathname change. Hash writes (report section tabs, deep-link
 *     filters) and query-string writes must not move the page.
 *   - Not on POP. Back and Forward keep the browser's own restored position,
 *     which is what a reader expects when retracing their steps.
 *   - Not when the new URL names an anchor: that page scrolls to the anchor
 *     itself, and jumping to the top first would fight it.
 *   - Not on the first render, so a deep link or a refresh keeps the position
 *     the browser chose.
 */
export function useScrollResetOnNavigate({ pathname, hash, navigationType }) {
    // The path last seen. Keyed on this rather than on the effect's deps: a
    // filter write is `navigate(..., { replace: true })` on the same path, which
    // changes only the navigation type, and AppBody re-pushes the current route
    // on mount — neither is a route change.
    const lastPathname = useRef(pathname);

    useEffect(() => {
        if (pathname === lastPathname.current) return;
        lastPathname.current = pathname;
        if (navigationType === 'POP') return;
        if (hash) return;
        if (typeof window === 'undefined') return;
        window.scrollTo(0, 0);
    }, [pathname, hash, navigationType]);
}
